// PreReleaseListings.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  Linking,
  Platform,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';

const LIST_API_URL = 'https://collabbay.com/APIControllers/GetPreReleaseListings.php';
const INTEREST_API_URL = 'https://collabbay.com/APIControllers/SendPreListInterest.php';

const PreReleaseListings = ({ navigation }) => {
  const isFocused = useIsFocused();

  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [userData, setUserData] = useState({});

  // info modal (tap "Not live yet")
  const [infoVisible, setInfoVisible] = useState(false);
  const [infoVenue, setInfoVenue] = useState(null);

  // interest modal
  const [interestVisible, setInterestVisible] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);

  // lightweight toast
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const openUrl = (url) => {
    if (!url) return;
    if (Platform.OS === 'web') window.open(url, '_blank', 'noopener,noreferrer');
    else Linking.openURL(url);
  };

  const safeParseToArray = (text) => {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.data)) return parsed.data;
      if (typeof parsed === 'string') {
        const again = JSON.parse(parsed);
        if (Array.isArray(again)) return again;
      }
      return [];
    } catch {
      return [];
    }
  };

  const fetchVenues = async () => {
    setLoading(true);
    try {
      const res = await fetch(LIST_API_URL, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      const text = await res.text();
      const arr = safeParseToArray(text);
      setVenues(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.error('PreReleaseListings fetch error:', e);
      setVenues([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await fetch('https://collabbay.com/APIControllers/GetSessionData.php', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      const result = await response.json();
      setUserData(result || {});
    } catch (e) {
      console.error('GetSessionData error:', e);
      setUserData({});
    }
  };

  useEffect(() => {
    fetchVenues();
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFocused) fetchVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  const filteredVenues = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return venues;
    return (venues || []).filter((v) => String(v?.BrandName || '').toLowerCase().includes(q));
  }, [venues, searchQuery]);

  const handlePersonIconPress = () => {
    if (userData?.Username) navigation.navigate('UserRecapPage');
    else navigation.navigate('LogInPage');
  };

  const openInfoModal = (venue) => {
    setInfoVenue(venue);
    setInfoVisible(true);
  };

  const closeInfoModal = () => {
    setInfoVisible(false);
    setInfoVenue(null);
  };

  const openInterestModal = (venue) => {
    setSelectedVenue(venue);
    setInterestVisible(true);
  };

  const closeInterestModal = () => {
    setInterestVisible(false);
    setSelectedVenue(null);
  };

  const submitInterest = async (timeframe) => {
    // IMPORTANT: close modal immediately after user selects
    const venue = selectedVenue;
    closeInterestModal();

    if (!venue?.BrandName) {
      showToast('Missing venue');
      return;
    }

    showToast('Sending…');

    try {
      const payload = {
        brandName: venue.BrandName,
        brandURL: venue.BrandURL || '',
        timeframe, // "1w" | "2w"
      };

      const res = await fetch(INTEREST_API_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let out;
      try {
        out = JSON.parse(text);
      } catch {
        out = { success: false, message: text || 'Invalid response' };
      }

      if (out?.success) {
        showToast('Sent ✅');
      } else {
        console.error('SendPreListInterest failed:', out);
        showToast(out?.message || 'Could not send');
      }
    } catch (e) {
      console.error('SendPreListInterest error:', e);
      showToast('Network error');
    }
  };

  const renderVenue = ({ item }) => {
    const brand = item?.BrandName || 'Untitled venue';
    const url = item?.BrandURL || '';

    return (
      <View style={styles.card}>
        <View style={styles.cardTop}>
          <Text style={styles.brandName} numberOfLines={1}>
            {brand}
          </Text>

          <TouchableOpacity
            onPress={() => openInfoModal(item)}
            activeOpacity={0.7}
            style={styles.infoChip}
          >
            <Ionicons name="information-circle-outline" size={16} color="#666" />
            <Text style={styles.infoChipText}>Not live yet</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.actionsRow}>
          <TouchableOpacity
            style={[styles.secondaryBtn, !url && { opacity: 0.45 }]}
            activeOpacity={0.85}
            onPress={() => openUrl(url)}
            disabled={!url}
          >
            <Ionicons name="open-outline" size={18} color="#000" />
            <Text style={styles.btnText}>See more</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.primaryBtn}
            activeOpacity={0.85}
            onPress={() => openInterestModal(item)}
          >
            <Ionicons name="heart-outline" size={18} color="#000" />
            <Text style={styles.btnText}>I’m interested</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff' }}>
      <SafeAreaView style={[styles.container, { width: '100%', maxWidth: 600, alignSelf: 'center', flex: 1 }]}>
        <View style={styles.contentContainer}>
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()} activeOpacity={0.7}>
              <Ionicons name="arrow-back" size={24} color="#000" />
            </TouchableOpacity>

            <View style={{ flex: 1 }}>
              <Text style={styles.title}>Pre-release venues</Text>
              <Text style={styles.subtitle}>Tap “I’m interested” to register for pre-release offers. </Text>
            </View>
          </View>

          {!!toast && (
            <View style={styles.toast}>
              <Text style={styles.toastText}>{toast}</Text>
            </View>
          )}

          {/* Search */}
          <View style={styles.searchWrap}>
            <Ionicons name="search-outline" size={18} color="#888" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search venues…"
              value={searchQuery}
              onChangeText={setSearchQuery}
              maxLength={120}
              placeholderTextColor="#9a9a9a"
            />
            {!!searchQuery && (
              <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearBtn} activeOpacity={0.75}>
                <Ionicons name="close-circle" size={18} color="#aaa" />
              </TouchableOpacity>
            )}
          </View>

          {/* List */}
          {loading ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator size="large" color="#000" />
              <Text style={styles.loadingText}>Loading…</Text>
            </View>
          ) : (
            <FlatList
              data={filteredVenues}
              renderItem={renderVenue}
              keyExtractor={(item, idx) => String(item?.ListingID || item?.BrandName || idx)}
              contentContainerStyle={styles.list}
              ListEmptyComponent={
                <View style={styles.emptyWrap}>
                  <Text style={styles.emptyTitle}>No results</Text>
                  <Text style={styles.emptySub}>Try another venue name.</Text>
                </View>
              }
            />
          )}
        </View>

        {/* INFO MODAL */}
        <Modal visible={infoVisible} transparent animationType="fade" onRequestClose={closeInfoModal}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalCard}>
              <View style={styles.modalTopRow}>
                <Text style={styles.modalTitle}>Not live yet</Text>
                <TouchableOpacity onPress={closeInfoModal} activeOpacity={0.7}>
                  <Ionicons name="close" size={22} color="#000" />
                </TouchableOpacity>
              </View>

              <Text style={styles.modalBody}>
                These venues have not listed offers yet, but you can nominate locations you lik,  and pre-register your interest.
                {'\n\n'}

              </Text>

              <TouchableOpacity
                style={styles.modalPrimary}
                activeOpacity={0.85}
                onPress={() => {
                  const v = infoVenue;
                  closeInfoModal();
                  if (v) openInterestModal(v);
                }}
              >
                <Ionicons name="heart-outline" size={18} color="#000" />
                <Text style={styles.modalPrimaryText}>Pre-register interest</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.modalSecondary} activeOpacity={0.85} onPress={closeInfoModal}>
                <Text style={styles.modalSecondaryText}>Got it</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>

        {/* INTEREST MODAL */}
        <Modal visible={interestVisible} transparent animationType="fade" onRequestClose={closeInterestModal}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalCard}>
              <View style={styles.modalTopRow}>
                <Text style={styles.modalTitle}>When do you want to visit?</Text>
                <TouchableOpacity onPress={closeInterestModal} activeOpacity={0.7}>
                  <Ionicons name="close" size={22} color="#000" />
                </TouchableOpacity>
              </View>

              <Text style={styles.modalSub}>
                Venue: <Text style={{ fontWeight: '900' }}>{selectedVenue?.BrandName || '—'}</Text>
              </Text>

              <TouchableOpacity style={styles.modalOption} activeOpacity={0.85} onPress={() => submitInterest('1w')}>
                <Ionicons name="time-outline" size={18} color="#000" />
                <Text style={styles.modalOptionText}>Within 1 week</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.modalOption} activeOpacity={0.85} onPress={() => submitInterest('2w')}>
                <Ionicons name="calendar-outline" size={18} color="#000" />
                <Text style={styles.modalOptionText}>Within 2 weeks</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>

        {/* Footer */}
        <View style={styles.footer}>
          <TouchableOpacity style={styles.footerIcon} onPress={() => navigation.navigate('Homepage')}>
            <Ionicons name="home" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity style={styles.footerIcon} onPress={() => navigation.navigate('CollabCalendar')}>
            <Ionicons name="calendar" size={24} color="black" />
          </TouchableOpacity>

          {userData.Username ? (
            <TouchableOpacity
              onPress={() => navigation.navigate('MyFeed')}
              style={{ backgroundColor: '#fdf05d', padding: 10, borderRadius: 20 }}
            >
              <Ionicons name="film-outline" size={22} color="black" />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              onPress={() => (window.location.href = 'https://collabbay.com/SignIn')}
              style={{ backgroundColor: '#fdf05d', padding: 10, borderRadius: 20 }}
            >
              <Ionicons name="film-outline" size={22} color="black" />
            </TouchableOpacity>
          )}

          <TouchableOpacity style={styles.footerIcon} onPress={() => navigation.navigate('UserActivityFeed')}>
            <Ionicons name="chatbox-ellipses-outline" size={24} color="black" />
          </TouchableOpacity>

          {userData.Username ? (
            <TouchableOpacity style={styles.footerIcon} onPress={handlePersonIconPress}>
              <Ionicons name="person" size={24} color="black" />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity style={styles.footerIcon} onPress={() => navigation.navigate('LogInPage')}>
              <Ionicons name="person" size={24} color="black" />
            </TouchableOpacity>
          )}
        </View>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  contentContainer: { paddingHorizontal: 18, paddingTop: 18, flex: 1 },

  header: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  backBtn: {
    width: 40,
    height: 40,
    borderRadius: 14,
    backgroundColor: '#f3f3f3',
    borderWidth: 1,
    borderColor: '#e7e7e7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: { fontSize: 22, fontWeight: '900', color: '#000' },
  subtitle: { marginTop: 3, fontSize: 12, fontWeight: '700', color: '#777' },

  toast: {
    backgroundColor: '#111',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 14,
    marginBottom: 10,
  },
  toastText: { color: '#fff', fontWeight: '900', fontSize: 12, textAlign: 'center' },

  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    height: 44,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#e6e6e6',
    backgroundColor: '#fafafa',
    paddingHorizontal: 12,
    marginBottom: 10,
  },
  searchInput: { flex: 1, fontSize: 14, fontWeight: '700', color: '#000' },
  clearBtn: { padding: 2 },

  list: { paddingTop: 6, paddingBottom: 92 },

  card: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 7 },
    shadowOpacity: 0.06,
    shadowRadius: 14,
    elevation: 2,
  },
  cardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  brandName: { flex: 1, fontSize: 18, fontWeight: '900', color: '#000' },

  infoChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#f3f3f3',
    borderWidth: 1,
    borderColor: '#e7e7e7',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
  },
  infoChipText: { fontSize: 11, fontWeight: '900', color: '#666' },

  actionsRow: { marginTop: 12, flexDirection: 'row', gap: 10 },

  secondaryBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#f3f3f3',
    borderWidth: 1,
    borderColor: '#e7e7e7',
    paddingVertical: 11,
    borderRadius: 14,
  },

  // ✅ removed black border as requested
  primaryBtn: {
    width: 170,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#fdf05d',
    borderWidth: 0,
    paddingVertical: 11,
    borderRadius: 14,
  },

  btnText: { fontSize: 13, fontWeight: '900', color: '#000' },

  loadingWrap: { marginTop: 26, alignItems: 'center' },
  loadingText: { marginTop: 10, fontSize: 12, fontWeight: '800', color: '#777' },

  emptyWrap: { paddingTop: 28, alignItems: 'center' },
  emptyTitle: { fontSize: 16, fontWeight: '900', color: '#000', marginBottom: 6 },
  emptySub: { fontSize: 12, fontWeight: '700', color: '#777' },

  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    padding: 18,
  },
  modalCard: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#eee',
  },
  modalTopRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  modalTitle: { fontSize: 16, fontWeight: '900', color: '#000' },
  modalSub: { marginTop: 10, fontSize: 12, fontWeight: '800', color: '#666' },
  modalBody: { marginTop: 10, fontSize: 13, fontWeight: '700', color: '#444', lineHeight: 18 },

  modalOption: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#f3f3f3',
    borderWidth: 1,
    borderColor: '#e7e7e7',
    paddingVertical: 12,
    borderRadius: 14,
  },
  modalOptionText: { fontSize: 13, fontWeight: '900', color: '#000' },

  modalPrimary: {
    marginTop: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#fdf05d',
    paddingVertical: 12,
    borderRadius: 14,
  },
  modalPrimaryText: { fontSize: 13, fontWeight: '900', color: '#000' },

  modalSecondary: {
    marginTop: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e7e7e7',
    paddingVertical: 11,
    borderRadius: 14,
  },
  modalSecondaryText: { fontSize: 13, fontWeight: '900', color: '#333' },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    left: 0,
    right: 0,
    paddingVertical: 10,
  },
  footerIcon: { alignItems: 'center', justifyContent: 'center' },
});

export default PreReleaseListings;
